/**
 * ADR Reference Service
 * Service for managing ADR (hazardous materials) reference data
 */

import { apiFetch } from '../utils/apiFetch'

// Types
export interface ADRClass {
  id: string
  tenant_id: string
  category: string
  code: string
  label: string
  description: string
  metadata: {
    risk_level: 'critical' | 'high' | 'medium' | 'low'
    default_separation_distance: number
    requires_special_handling: boolean
    label_short?: string
  }
  is_system: boolean
  is_active: boolean
  sort_order: number
  created_at: string
  updated_at: string
}

export interface UNNumber {
  id: string
  tenant_id: string
  category: string
  code: string
  label: string
  description: string
  metadata: {
    adr_class: string
    packing_group: string
    label_short: string
  }
  is_system: boolean
  is_active: boolean
  sort_order: number
  created_at: string
  updated_at: string
}

export interface CompatibilityRule {
  id: string
  tenant_id: string
  category: string
  code: string
  label: string
  description: string
  metadata: {
    class1: string
    class2: string
    separation_distance: number
    is_critical: boolean
    reason: string
  }
  is_system: boolean
  is_active: boolean
  sort_order: number
  created_at: string
  updated_at: string
}

export interface CreateCompatibilityRuleInput {
  class1: string
  class2: string
  separation_distance?: number
  is_critical?: boolean
  reason?: string
}

export interface UpdateCompatibilityRuleInput {
  separation_distance?: number
  is_critical?: boolean
  reason?: string
  description?: string
}

export interface CreateUNNumberInput {
  code: string
  label: string
  description?: string
  adr_class?: string
  packing_group?: string
}

export interface UpdateUNNumberInput {
  label?: string
  description?: string
  adr_class?: string
  packing_group?: string
  label_short?: string
}

class ADRReferenceService {
  private readonly baseUrl = '/api/internal/tms/v1/adr'

  /**
   * Get all ADR classes
   */
  async getADRClasses(activeOnly: boolean = true): Promise<ADRClass[]> {
    const response = await apiFetch(`${this.baseUrl}/classes?activeOnly=${activeOnly}`)
    return response.json()
  }

  /**
   * Get a specific ADR class by code
   */
  async getADRClass(code: string): Promise<ADRClass> {
    const response = await apiFetch(`${this.baseUrl}/classes/${code}`)
    return response.json()
  }

  /**
   * Get UN numbers, optionally filtered by ADR class
   */
  async getUNNumbers(adrClass?: string): Promise<UNNumber[]> {
    const url = adrClass
      ? `${this.baseUrl}/un-numbers?adrClass=${encodeURIComponent(adrClass)}`
      : `${this.baseUrl}/un-numbers`
    const response = await apiFetch(url)
    return response.json()
  }

  /**
   * Create a new UN number
   */
  async createUNNumber(data: CreateUNNumberInput): Promise<UNNumber> {
    const response = await apiFetch(`${this.baseUrl}/un-numbers`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
    return response.json()
  }

  /**
   * Update an existing UN number
   */
  async updateUNNumber(id: string, data: UpdateUNNumberInput): Promise<UNNumber> {
    const response = await apiFetch(`${this.baseUrl}/un-numbers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
    return response.json()
  }

  /**
   * Delete a UN number (soft delete)
   */
  async deleteUNNumber(id: string): Promise<UNNumber> {
    const response = await apiFetch(`${this.baseUrl}/un-numbers/${id}`, {
      method: 'DELETE',
    })
    return response.json()
  }

  /**
   * Get all compatibility rules
   */
  async getCompatibilityRules(activeOnly: boolean = true): Promise<CompatibilityRule[]> {
    const response = await apiFetch(`${this.baseUrl}/compatibility-rules?activeOnly=${activeOnly}`)
    return response.json()
  }

  /**
   * Create a new compatibility rule
   */
  async createCompatibilityRule(data: CreateCompatibilityRuleInput): Promise<CompatibilityRule> {
    const response = await apiFetch(`${this.baseUrl}/compatibility-rules`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
    return response.json()
  }

  /**
   * Update an existing compatibility rule
   */
  async updateCompatibilityRule(id: string, data: UpdateCompatibilityRuleInput): Promise<CompatibilityRule> {
    const response = await apiFetch(`${this.baseUrl}/compatibility-rules/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
    return response.json()
  }

  /**
   * Delete a compatibility rule (soft delete)
   */
  async deleteCompatibilityRule(id: string): Promise<CompatibilityRule> {
    const response = await apiFetch(`${this.baseUrl}/compatibility-rules/${id}`, {
      method: 'DELETE',
    })
    return response.json()
  }

  /**
   * Get incompatible pairs (for internal use)
   */
  async getIncompatiblePairs(): Promise<string[][]> {
    const response = await apiFetch(`${this.baseUrl}/incompatible-pairs`)
    return response.json()
  }

  /**
   * Get separation distance for a specific class combination
   */
  async getSeparationDistance(class1: string, class2: string, defaultDistance?: number): Promise<{ distance: number; class1: string; class2: string }> {
    const params = new URLSearchParams({ class1, class2 })
    if (defaultDistance !== undefined) params.set('defaultDistance', String(defaultDistance))
    const url = `${this.baseUrl}/separation-distance?${params}`
    const response = await apiFetch(url)
    return response.json()
  }

  /**
   * Check if a combination is critical
   */
  async isCriticalCombination(class1: string, class2: string): Promise<{ isCritical: boolean; class1: string; class2: string }> {
    const url = `${this.baseUrl}/critical-combination?class1=${encodeURIComponent(class1)}&class2=${encodeURIComponent(class2)}`
    const response = await apiFetch(url)
    return response.json()
  }

  /**
   * Helper to format ADR class options for dropdowns
   */
  async getADRClassOptions(): Promise<{ value: string; label: string }[]> {
    const classes = await this.getADRClasses()
    return classes.map(cls => ({
      value: cls.code,
      label: `${cls.code} — ${cls.metadata.label_short || cls.label}`
    }))
  }
}

export const adrReferenceService = new ADRReferenceService()
